































































import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { State } from 'vuex-class';
import {
  FilteredGetGroup,
  Group,
  GroupType
} from '@/store/modules/admin/types/admin.types';
import { RootState } from '@/store/store';
import SelectGroupList from './select-group-list/SelectGroupList.vue';
import BasePaginatorHoc from '@/components/base/BasePaginatorHoc.vue';
import {
  getFilteredGroups,
  getDescendantsWithRebasedPaths,
  getPaginatedFilteredGroups,
  getTotalCount
} from '@/utils/group.util';
import { Pagination } from 'nestjs-typeorm-paginate';
import { PaginatorSpecs, SortEventData } from '@/store/types/general.types';

const filterParamsInitial: FilteredGetGroup = {
  groupname: '',
  grouptype: ''
};

@Component({
  components: {
    BasePaginatorHoc
  }
})
export default class SelectedGroupModal extends Vue {
  @Prop(Array) public allSelectedGroups!: Group[];
  @Prop(Array) public groupsToDisable!: number[];

  public perPage: number = 50;
  public page: number = 1;
  public sortColumn: string = 'name';
  public sortOrder: 'ASC' | 'DESC' = 'ASC';
  public filterParams: FilteredGetGroup = { ...filterParamsInitial };
  public lastFilterParams: FilteredGetGroup = { ...filterParamsInitial };
  public expandGroupFilter: boolean = true;
  public selectedGroupIds: number[] = [];
  public totalCount: number = 0;

  /**
   * Array of groups from both existing selected groups from database
   * AND selected groups from users in `SelectGroupStep`.
   */
  allGroups: Group[] = [];
  paginatedFilteredGroups: Group[] = [];

  @State(({ admin }: RootState) => admin.groups?.items)
  groups!: Group[];

  @State(({ admin }: RootState) => admin.groupTypes)
  public groupTypes!: GroupType[];

  @State(({ admin }: RootState) => admin.groupsByUserAttribute)
  public groupsByUserAttribute!: Pagination<Group>;

  @State(
    ({ admin }: RootState) => admin.highestDirectActiveGroupsByUserAttribute
  )
  public highestDirectActiveGroupsByUserAttribute!: Group[];

  get typeOptions():
    | Array<{
        id: number;
        name: string;
        slug: string;
      }>
    | undefined {
    if (this.groupTypes) {
      const types = Object.values(this.groupTypes).map(
        (groupType: GroupType) => ({
          id: groupType.id,
          slug: groupType.name,
          name: groupType.name
        })
      );
      return [{ id: 0, name: 'All', slug: '' }, ...types];
    }
  }

  get SelectGroupList(): typeof SelectGroupList {
    return SelectGroupList;
  }

  get initialState(): {
    groupname: string;
    grouptype: string;
  } {
    return {
      groupname: '',
      grouptype: ''
    };
  }

  public updateSelectedGroup(): void {
    this.$emit('updateSelectedGroup', this.selectedGroupIds);
    this.closeModal();
  }

  public handleSort(data: SortEventData): void {
    this.sortColumn = data.sortColumn;
    this.sortOrder = data.sortOrder;

    switch (this.sortColumn) {
      case 'name':
        if (this.sortOrder === 'ASC') {
          this.paginatedFilteredGroups = this.allGroups.sort((a, b) => {
            return ('' + a.name).localeCompare(b.name);
          });
        } else {
          this.paginatedFilteredGroups = this.allGroups.sort((a, b) => {
            return ('' + b.name).localeCompare(a.name);
          });
        }
        break;
      case 'group_type':
        if (this.sortOrder === 'ASC') {
          this.paginatedFilteredGroups = this.allGroups.sort((a, b) => {
            return ('' + a.types.name).localeCompare(b.types.name);
          });
        } else {
          this.paginatedFilteredGroups = this.allGroups.sort((a, b) => {
            return ('' + b.types.name).localeCompare(a.types.name);
          });
        }
        break;
      default:
        break;
    }
    this.handlePaginator({ perPage: this.perPage, page: this.page });
  }

  public async handlePaginator(paginator: PaginatorSpecs) {
    const { page, perPage } = paginator;
    this.page = page;
    this.expandGroupFilter = true;

    const filteredGroups = getFilteredGroups(this.allGroups, this.filterParams);

    this.paginatedFilteredGroups = getPaginatedFilteredGroups(
      filteredGroups,
      perPage,
      page
    );

    this.totalCount = getTotalCount(
      filteredGroups,
      this.allGroups,
      this.filterParams
    );

    if (
      this.filterParams.groupname?.length === 0 &&
      this.selectedGroupIds.length === 0
    ) {
      this.expandGroupFilter = false;
    }
  }

  public closeModal(): void {
    this.$emit('close');
  }

  getChildren(group: Group): Group[] {
    const path: string = group.path;
    return this.groups.filter(
      (groupItem) => groupItem.path === path + '.' + groupItem.id
    );
  }

  mounted(): void {
    this.selectedGroupIds = this.allSelectedGroups.map((group) => group.id);
    /**
     * When parent groups are selected, `selectedGroups` array is only
     * updated with parent group. Subgroups are not included. This flatMap
     * maps all potential children from the subgroups into a separate
     * array.
     */
    const getSelectedGroupChildren = this.allSelectedGroups.flatMap(
      (selectedGroup) => this.getChildren(selectedGroup)
    );

    const combinedGroups: Group[] = this.filterGroupByAncestors([
      ...this.groupsByUserAttribute.items,
      ...this.allSelectedGroups,
      ...getSelectedGroupChildren
    ]);

    this.allGroups = combinedGroups;

    // Only paginate after the groups have been assigned
    this.handlePaginator({ perPage: this.perPage, page: this.page });
  }

  /**
   * For each selected group, finds all its descendant groups
   * from a provided list, adjusts their paths to be relative,
   * calculates their depth, and returns a unique list.
   *
   * @returns A de-duplicated array of descendant groups with
   *          modified paths and nlevel
   */
  public filterGroupByAncestors(
    allGroups: Group[],
    selectedGroups?: Group[]
  ): Group[] {
    if (this.selectedGroupIds.length === 0) {
      return [];
    }

    // Determine the source of selected groups to iterate over
    const groupsToProcess = selectedGroups || this.allSelectedGroups;

    const allDescendants = getDescendantsWithRebasedPaths(
      allGroups,
      groupsToProcess
    );

    // De-duplicate results
    return this.getUniqGroups(allDescendants);
  }

  public getUniqGroups(groups: Group[]): Group[] {
    return [...new Map(groups.map((group) => [group.id, group])).values()];
  }

  @Watch('filterParams', { deep: true })
  public onChange(): void {
    this.handlePaginator({ perPage: this.perPage, page: this.page });
  }
}
